/**
 * Composable Vue partagé pour l'état d'authentification.
 *
 * Persistance : localStorage (clés `sapiendo.auth.access`, `sapiendo.auth.refresh`, `sapiendo.auth.user`).
 * Pas de Pinia : un état réactif global suffit pour ce périmètre.
 *
 * Le stockage est la SOURCE DE VÉRITÉ de la session ; les refs n'en sont qu'un miroir réactif, relu avant chaque
 * usage (`syncFromStorage`). Montée dans la sandbox (route /ggvie), cette copie partage ces clés avec
 * l'authentification du shell, qui les écrit sans passer par elle.
 */

import { computed, ref } from 'vue'

import { resetAllGatheringStates } from '@ggvie-consulting/lib/gatheringArtifactStore'
import { resetAllPersistedStates } from '@ggvie-consulting/lib/persistedArtifactsStore'
import { STORAGE_RETURN_TO } from '@ggvie-consulting/maintenance/storageKeys'
import { STORAGE_KEY_PREFIX as SPECTATOR_PREFIX } from '@ggvie-consulting/spectator/useSpectatorMode'

import type { AuthUser, TokenResponse } from './types'

/**
 * Reset les states partagés qui peuvent porter des données du précédent user.
 * À appeler dès qu'on change d'identité : login, logout, enter/exit impersonation.
 * Ne pas inclure le state d'auth lui-même — il est mis à jour par les fonctions appelantes.
 */
// Préférences persistées rattachées à UN conseiller : elles survivent au logout, donc sur
// un poste partagé le suivant en hériterait. `STORAGE_RETURN_TO` est le cas le plus visible
// — en sortie de maintenance il pousse le nouvel arrivant vers la conversation de l'ancien.
const USER_SCOPED_KEYS = [STORAGE_RETURN_TO, 'customer-case-panel-mode']
const USER_SCOPED_PREFIXES = [SPECTATOR_PREFIX]

function clearUserScopedStores(): void {
  resetAllGatheringStates()
  resetAllPersistedStates()
  try {
    for (const key of USER_SCOPED_KEYS) localStorage.removeItem(key)
    for (const key of Object.keys(localStorage)) {
      if (USER_SCOPED_PREFIXES.some((prefix) => key.startsWith(prefix))) localStorage.removeItem(key)
    }
  } catch {
    // localStorage indisponible (navigation privée stricte) : sans conséquence ici.
  }
}

const STORAGE_ACCESS = 'sapiendo.auth.access'
const STORAGE_REFRESH = 'sapiendo.auth.refresh'
const STORAGE_USER = 'sapiendo.auth.user'
// Impersonation : on garde les tokens admin originaux pour pouvoir y revenir.
const STORAGE_IMP_ACCESS = 'sapiendo.auth.impersonator.access'
const STORAGE_IMP_REFRESH = 'sapiendo.auth.impersonator.refresh'
const STORAGE_IMP_USER = 'sapiendo.auth.impersonator.user'

function parseUser(raw: string | null): AuthUser | null {
  if (!raw) return null
  try {
    return JSON.parse(raw) as AuthUser
  } catch {
    return null
  }
}

// Utilisateurs sous leur forme stockée : le miroir ne reconstruit un objet (et ne réveille la réactivité) que
// lorsque le stockage en porte un autre.
let userJson = localStorage.getItem(STORAGE_USER)
let impersonatorJson = localStorage.getItem(STORAGE_IMP_USER)

const accessToken = ref<string | null>(localStorage.getItem(STORAGE_ACCESS))
const refreshToken = ref<string | null>(localStorage.getItem(STORAGE_REFRESH))
const user = ref<AuthUser | null>(parseUser(userJson))
const impersonator = ref<AuthUser | null>(parseUser(impersonatorJson))

const isAuthenticated = computed(() => accessToken.value !== null && user.value !== null)
const isAdmin = computed(() => user.value?.is_admin === true)
const isImpersonating = computed(() => impersonator.value !== null)

interface StoredSession {
  access: string | null
  refresh: string | null
  user: string | null
  impersonator: string | null
}

function readStoredSession(): StoredSession | null {
  try {
    return {
      access: localStorage.getItem(STORAGE_ACCESS),
      refresh: localStorage.getItem(STORAGE_REFRESH),
      user: localStorage.getItem(STORAGE_USER),
      impersonator: localStorage.getItem(STORAGE_IMP_USER),
    }
  } catch {
    // Stockage illisible : le miroir reste la seule copie disponible.
    return null
  }
}

/**
 * Aligne le miroir sur le stockage. Montée dans la sandbox, la vue partage les clés `sapiendo.auth.*` avec le shell,
 * qui les écrit à la connexion, au refresh et à la déconnexion sans passer par ce module. Lu une seule fois au
 * chargement, le miroir se périmait : après une session perdue (refs vidées ici) puis une reconnexion par le
 * formulaire du shell, les requêtes de la vue partaient sans jeton et `_doRefresh`, faute de refresh, effaçait la
 * session toute neuve — boucle de reconnexion. Appelée avant chaque lecture des jetons et à chaque `useAuth()`. En
 * application autonome, ce module est seul à écrire dans l'onglet : rien ne change, sinon qu'une connexion ou une
 * déconnexion faite dans un autre onglet est reprise.
 */
function syncFromStorage(): void {
  const stored = readStoredSession()
  if (!stored) return
  if (stored.user !== userJson) {
    const next = parseUser(stored.user)
    userJson = stored.user
    // Identité changée hors de ce module (autre conseiller, déconnexion) : même purge qu'à la connexion et à la
    // déconnexion. La même personne (refresh, reconnexion) garde son état.
    if ((next?.id ?? null) !== (user.value?.id ?? null)) clearUserScopedStores()
    user.value = next
  }
  if (stored.impersonator !== impersonatorJson) {
    impersonatorJson = stored.impersonator
    impersonator.value = parseUser(stored.impersonator)
  }
  if (accessToken.value !== stored.access) accessToken.value = stored.access
  if (refreshToken.value !== stored.refresh) refreshToken.value = stored.refresh
}

function writeKey(key: string, value: string | null): void {
  if (value) localStorage.setItem(key, value)
  else localStorage.removeItem(key)
}

/** Écrit toute la session : réservé aux changements complets (connexion, déconnexion, impersonation). */
function persist(): void {
  userJson = user.value ? JSON.stringify(user.value) : null
  writeKey(STORAGE_ACCESS, accessToken.value)
  writeKey(STORAGE_REFRESH, refreshToken.value)
  writeKey(STORAGE_USER, userJson)
}

export function setSession(payload: TokenResponse): void {
  clearUserScopedStores()
  accessToken.value = payload.access_token
  refreshToken.value = payload.refresh_token
  user.value = payload.user
  persist()
}

export function clearSession(): void {
  clearUserScopedStores()
  accessToken.value = null
  refreshToken.value = null
  user.value = null
  impersonator.value = null
  impersonatorJson = null
  localStorage.removeItem(STORAGE_IMP_ACCESS)
  localStorage.removeItem(STORAGE_IMP_REFRESH)
  localStorage.removeItem(STORAGE_IMP_USER)
  persist()
}

/**
 * Bascule sur la session du user cible. Conserve les tokens admin courants pour pouvoir revenir.
 */
export function enterImpersonation(payload: TokenResponse): void {
  syncFromStorage()
  // Sauvegarde l'admin actuel (s'il y en a un) — sera restauré au exitImpersonation()
  if (accessToken.value && refreshToken.value && user.value) {
    impersonatorJson = JSON.stringify(user.value)
    localStorage.setItem(STORAGE_IMP_ACCESS, accessToken.value)
    localStorage.setItem(STORAGE_IMP_REFRESH, refreshToken.value)
    localStorage.setItem(STORAGE_IMP_USER, impersonatorJson)
    impersonator.value = user.value
  }
  clearUserScopedStores()
  accessToken.value = payload.access_token
  refreshToken.value = payload.refresh_token
  user.value = payload.user
  persist()
}

/**
 * Restaure la session admin originale. Retourne true si une impersonation était en cours.
 */
export function exitImpersonation(): boolean {
  const access = localStorage.getItem(STORAGE_IMP_ACCESS)
  const refresh = localStorage.getItem(STORAGE_IMP_REFRESH)
  const userRaw = localStorage.getItem(STORAGE_IMP_USER)
  if (!access || !refresh || !userRaw) return false
  try {
    const adminUser = JSON.parse(userRaw) as AuthUser
    clearUserScopedStores()
    accessToken.value = access
    refreshToken.value = refresh
    user.value = adminUser
  } catch {
    return false
  }
  impersonator.value = null
  impersonatorJson = null
  localStorage.removeItem(STORAGE_IMP_ACCESS)
  localStorage.removeItem(STORAGE_IMP_REFRESH)
  localStorage.removeItem(STORAGE_IMP_USER)
  persist()
  return true
}

export function getAccessToken(): string | null {
  syncFromStorage()
  return accessToken.value
}

export function getRefreshToken(): string | null {
  syncFromStorage()
  return refreshToken.value
}

// Une écriture ne touche que sa clé : réécrire toute la session reposerait, par-dessus une paire renouvelée
// entre-temps par le shell (refresh pendant une requête de la vue), un refresh déjà consommé — que le serveur
// prendrait pour un vol au refresh suivant (révocation de toute la chaîne).
export function setAccessToken(token: string): void {
  accessToken.value = token
  writeKey(STORAGE_ACCESS, token)
}

export function setRefreshToken(token: string): void {
  refreshToken.value = token
  writeKey(STORAGE_REFRESH, token)
}

export function setUser(u: AuthUser): void {
  user.value = u
  userJson = JSON.stringify(u)
  writeKey(STORAGE_USER, userJson)
}

export function useAuth(): {
  user: typeof user
  impersonator: typeof impersonator
  accessToken: typeof accessToken
  isAuthenticated: typeof isAuthenticated
  isAdmin: typeof isAdmin
  isImpersonating: typeof isImpersonating
  setSession: typeof setSession
  clearSession: typeof clearSession
  enterImpersonation: typeof enterImpersonation
  exitImpersonation: typeof exitImpersonation
} {
  syncFromStorage()
  return {
    user,
    impersonator,
    accessToken,
    isAuthenticated,
    isAdmin,
    isImpersonating,
    setSession,
    clearSession,
    enterImpersonation,
    exitImpersonation,
  }
}
